'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import womanSign from '../../assets/info/woman-doing-sign-language.png';
import pplWork from '../../assets/info/people-smiling-and-working.png';
import womanLook from '../../assets/info/woman-looking-at-camera.png';

const pilares = [
	{
		title: 'Conexão',
		description:
			'Conectamos pessoas com deficiência e empresas que valorizam a inclusão. Esse encontro traz visibilidade às ações de diversidade e reforço de marca empregadora inclusiva.',
		buttonText: 'Conheça nossos planos',
		buttonHref: '/cadastroempresa#beneficios',
		image: womanSign,
		imageAlt: 'Mulher falando em linguagem de sinais',
		imagePosition: 'left' as const,
	},
	{
		title: 'Inclusão',
		description:
			'Buscamos equidade de oportunidades para pessoas com deficiência e uma avaliação pelo seu potencial. Assim, queremos que esses encontros se transformem em inclusões.',
		buttonText: 'Candidato cadastre-se',
		buttonHref: '/candidato/cadastrar',
		image: pplWork,
		imageAlt: 'Pessoas trabalhando e rindo',
		imagePosition: 'right' as const,
	},
	{
		title: 'Capacitação',
		description:
			'Unimos empresas e pessoas com deficiência para compartilhar experiências e conhecimentos. O resultado é a geração de conteúdo relevante sobre inclusão.',
		buttonText: 'Assista conteúdos',
		buttonHref: '/programacao',
		image: womanLook,
		imageAlt: 'Mulher usando laptop, olhando para a câmera e sorrindo',
		imagePosition: 'left' as const,
	},
];

const ChevronLeft = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="m15 18-6-6 6-6" />
	</svg>
);

const ChevronRight = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="m9 18 6-6-6-6" />
	</svg>
);

export default function PilaresCarousel() {
	const [current, setCurrent] = useState(0);

	const prev = () => setCurrent((c) => (c === 0 ? pilares.length - 1 : c - 1));
	const next = () => setCurrent((c) => (c === pilares.length - 1 ? 0 : c + 1));

	const pilar = pilares[current];

	return (
		<section id="pilares" className="relative -mt-px overflow-hidden bg-[#f3f4f6]">
			<div className="mx-auto max-w-[1140px] px-6 pb-20 pt-[60px]">
				<h2 className="mb-[50px] text-center text-[28px] font-extrabold text-[#111827] md:text-[38px]">Nossos Pilares</h2>

				<div className="relative flex items-center">
					{/* Prev arrow */}
					<button
						onClick={prev}
						className="absolute left-[-8px] top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#374151] shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-[#f9fafb] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] md:left-[-16px]"
						aria-label="Slide anterior"
					>
						<ChevronLeft />
					</button>

					<div className="flex w-full flex-col items-center gap-10 md:flex-row md:gap-[60px]" key={current}>
						{pilar.imagePosition === 'left' ? (
							<>
								<div className="min-w-0 flex-1">
									<Image
										src={pilar.image}
										alt={pilar.imageAlt}
										className="h-auto max-h-[400px] w-full rounded-[20px] object-cover shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
										width={500}
										height={380}
									/>
								</div>
								<div className="min-w-0 flex-1">
									<h3 className="mb-4 text-[26px] font-extrabold text-[#111827] md:text-[32px]">{pilar.title}</h3>
									<p className="mb-6 text-base leading-[1.7] text-[#4b5563]">{pilar.description}</p>
									<Link
										href={pilar.buttonHref}
										className="inline-block rounded-[10px] bg-brand-main px-7 py-3.5 text-[15px] font-semibold text-white no-underline transition-all duration-300 hover:-translate-y-px hover:bg-brand-dark hover:shadow-[0_4px_16px_rgba(30,58,138,0.3)]"
									>
										{pilar.buttonText}
									</Link>
								</div>
							</>
						) : (
							<>
								<div className="order-2 min-w-0 flex-1 md:order-1">
									<h3 className="mb-4 text-[26px] font-extrabold text-[#111827] md:text-[32px]">{pilar.title}</h3>
									<p className="mb-6 text-base leading-[1.7] text-[#4b5563]">{pilar.description}</p>
									<Link
										href={pilar.buttonHref}
										className="inline-block rounded-[10px] bg-brand-main px-7 py-3.5 text-[15px] font-semibold text-white no-underline transition-all duration-300 hover:-translate-y-px hover:bg-brand-dark hover:shadow-[0_4px_16px_rgba(30,58,138,0.3)]"
									>
										{pilar.buttonText}
									</Link>
								</div>
								<div className="order-1 min-w-0 flex-1 md:order-2">
									<Image
										src={pilar.image}
										alt={pilar.imageAlt}
										className="h-auto max-h-[400px] w-full rounded-[20px] object-cover shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
										width={500}
										height={380}
									/>
								</div>
							</>
						)}
					</div>

					{/* Next arrow */}
					<button
						onClick={next}
						className="absolute right-[-8px] top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#374151] shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-[#f9fafb] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] md:right-[-16px]"
						aria-label="Próximo slide"
					>
						<ChevronRight />
					</button>
				</div>

				{/* Dots */}
				<div className="mt-10 flex justify-center gap-2">
					{pilares.map((_, i) => (
						<button
							key={i}
							onClick={() => setCurrent(i)}
							className={`h-2.5 cursor-pointer rounded-[5px] border-0 p-0 transition-all duration-300 ${
								i === current ? 'w-7 bg-brand-main' : 'w-2.5 bg-[#d1d5db]'
							}`}
							aria-label={`Ir para slide ${i + 1}`}
						/>
					))}
				</div>
			</div>

			{/* Bottom curved divider */}
			<div className="relative bg-brand-main">
				<svg
					viewBox="0 0 1440 80"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					preserveAspectRatio="none"
					className="block h-[60px] w-full"
				>
					<path d="M0,0 L0,80 Q720,20 1440,0 L1440,0 Z" fill="#f3f4f6" />
				</svg>
			</div>
		</section>
	);
}
